import { useState, useEffect } from "react";
import { X, CheckSquare, Loader2, Check, XCircle, Users, ChevronDown, Building2, Cpu, Search, UserCheck } from "lucide-react";
import {
  companyService,
  userService,
  modelConfigurationService,
  userModelGroupMappingService,
} from "../../../../services/index.js";

const readArray = (r) =>
  Array.isArray(r) ? r
  : Array.isArray(r?.data) ? r.data
  : Array.isArray(r?.data?.data) ? r.data.data
  : [];

// ── Styled Select ─────────────────────────────────────────────────────────────
function StyledSelect({ label, required, icon: Icon, value, onChange, disabled, loading, children, hint }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-2">
        {label} {required && <span className="text-red-500 normal-case">*</span>}
      </label>
      <div className="relative">
        {Icon && <Icon size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />}
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled || loading}
          className={`w-full ${Icon ? "pl-10" : "pl-4"} pr-9 py-2.5 rounded-xl border-2 ${
            value
              ? "border-[#1d57a5]/40 bg-[#1d57a5]/5 dark:bg-[#1d57a5]/20 text-slate-900 dark:text-white"
              : "border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white"
          } focus:border-[#1d57a5] focus:ring-2 focus:ring-[#1d57a5]/20 outline-none transition-all text-sm appearance-none disabled:opacity-50 disabled:cursor-not-allowed dark:[color-scheme:dark]`}
        >
          {children}
        </select>
        <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      </div>
      {loading && (
        <p className="text-xs text-[#1d57a5] mt-1.5 flex items-center gap-1.5">
          <span className="w-3 h-3 border-2 border-[#1d57a5] border-t-transparent rounded-full animate-spin inline-block" />
          Loading…
        </p>
      )}
      {hint && !loading && <p className="text-xs text-slate-400 mt-1.5 flex items-center gap-1">ℹ {hint}</p>}
    </div>
  );
}

// ── Step Badge ────────────────────────────────────────────────────────────────
function StepBadge({ number, label, active, done }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-all ${
        done ? "bg-[#36a852] text-white" :
        active ? "bg-[#1d57a5] text-white" :
        "bg-slate-200 dark:bg-slate-700 text-slate-500"
      }`}>
        {done ? <Check size={12} strokeWidth={3} /> : number}
      </div>
      <span className={`text-xs font-semibold ${active ? "text-[#1d57a5] dark:text-[#4a8ce3]" : done ? "text-slate-600 dark:text-slate-400" : "text-slate-400"}`}>
        {label}
      </span>
    </div>
  );
}

// ── Main Form ─────────────────────────────────────────────────────────────────
export default function AssignmentForm({ editingMapping, onCancel, onSuccess }) {
  const isEdit = !!editingMapping;
  const currentUserId = Number(localStorage.getItem("userId") ?? 0);

  const [companies,    setCompanies]    = useState([]);
  const [modelGroups,  setModelGroups]  = useState([]);
  const [users,        setUsers]        = useState([]);
  const [companyId,    setCompanyId]    = useState("");
  const [modelGroupId, setModelGroupId] = useState("");
  const [checkedUserIds, setCheckedUserIds] = useState([]);
  const [existingMappings, setExistingMappings] = useState([]);

  const [loadingCompanies,  setLoadingCompanies]  = useState(true);
  const [loadingGroups,     setLoadingGroups]     = useState(false);
  const [loadingUsers,      setLoadingUsers]      = useState(false);
  const [loadingMappings,   setLoadingMappings]   = useState(false);
  const [isSubmitting,      setIsSubmitting]      = useState(false);
  const [userSearch,        setUserSearch]        = useState("");

  const [toast, setToast] = useState({ visible: false, message: "", type: "success" });
  const showToast = (message, type = "success") => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast({ visible: false, message: "", type: "success" }), 3000);
  };

  // Step tracking
  const step1Done = !!companyId;
  const step2Done = !!modelGroupId;
  const activeStep = !companyId ? 1 : !modelGroupId ? 2 : 3;

  // Load companies
  useEffect(() => {
    (async () => {
      try {
        setLoadingCompanies(true);
        const res = await companyService.getAllCompanies();
        setCompanies(readArray(res).map(c => ({
          id:   String(c.companyID ?? c.companyId ?? c.id ?? ""),
          name: c.companyName ?? c.name ?? "Unknown",
        })));
      } catch (e) { console.error(e); }
      finally { setLoadingCompanies(false); }
    })();
  }, []);

  // Pre-fill on edit
  useEffect(() => {
    if (isEdit && editingMapping) {
      setCompanyId(String(editingMapping.companyId ?? ""));
      setModelGroupId(String(editingMapping.modelGroupId ?? ""));
    }
  }, [isEdit, editingMapping]);

  // Load model groups + users when company changes
  useEffect(() => {
    if (!companyId) {
      setModelGroups([]); setUsers([]);
      if (!isEdit) { setModelGroupId(""); setCheckedUserIds([]); }
      return;
    }
    (async () => {
      try {
        setLoadingGroups(true); setLoadingUsers(true);
        setModelGroups([]); setUsers([]);
        if (!isEdit) { setModelGroupId(""); setCheckedUserIds([]); }
        const [groupsRes, usersRes] = await Promise.all([
          modelConfigurationService.getModelGroupByCompanyId(companyId),
          userService.getAllUsers(companyId),
        ]);
        setModelGroups(readArray(groupsRes).map(g => ({ id: g.modelGroupId ?? g.id, name: g.name ?? "Unknown Group" })));
        setUsers(readArray(usersRes).map(u => ({
          id:    String(u.userId ?? u.id ?? ""),
          name:  u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.name ?? u.userName ?? "Unknown",
          email: u.email ?? u.officialEmail ?? "",
        })));
      } catch (e) { console.error(e); }
      finally { setLoadingGroups(false); setLoadingUsers(false); }
    })();
  }, [companyId]);

  // Load existing mappings when model group changes
  useEffect(() => {
    if (!modelGroupId) { setCheckedUserIds([]); setExistingMappings([]); return; }
    (async () => {
      try {
        setLoadingMappings(true);
        const res = await userModelGroupMappingService.getUserModelGroupMappingsByModelGroupId({ modelGroupId: Number(modelGroupId) });
        const mappings = readArray(res);
        setExistingMappings(mappings);
        setCheckedUserIds(mappings.filter(m => m.isActive).map(m => String(m.userId)));
      } catch (e) { console.error(e); setCheckedUserIds([]); setExistingMappings([]); }
      finally { setLoadingMappings(false); }
    })();
  }, [modelGroupId]);

  const toggleUser = (uid) => setCheckedUserIds(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);

  const toggleAll = () => {
    const filtered = filteredUsers;
    const allChecked = filtered.every(u => checkedUserIds.includes(u.id));
    if (allChecked) setCheckedUserIds(prev => prev.filter(id => !filtered.find(u => u.id === id)));
    else setCheckedUserIds(prev => [...new Set([...prev, ...filtered.map(u => u.id)])]);
  };

  const handleSubmit = async () => {
    if (!companyId)    { showToast("Please select a company", "error"); return; }
    if (!modelGroupId) { showToast("Please select a model group", "error"); return; }
    setIsSubmitting(true);
    try {
      const base = { companyId: Number(companyId), modelGroupId: Number(modelGroupId) };

      if (isEdit) {
        const existingUserIds = existingMappings.map(m => String(m.userId));
        const toActivate   = existingMappings.filter(m => checkedUserIds.includes(String(m.userId)) && !m.isActive).map(m => String(m.userId));
        const toDeactivate = existingMappings.filter(m => !checkedUserIds.includes(String(m.userId)) && m.isActive).map(m => String(m.userId));
        const toAdd        = checkedUserIds.filter(uid => !existingUserIds.includes(uid));

        if (toAdd.length > 0) {
          await userModelGroupMappingService.createUserModelGroupMapping({
            ...base, userIds: toAdd.map(Number), createdBy: currentUserId,
          });
        }
        if (toActivate.length > 0) {
          await userModelGroupMappingService.updateUserModelGroupMapping({
            ...base, userIds: toActivate.map(Number), isActive: true, updatedBy: currentUserId,
          });
        }
        if (toDeactivate.length > 0) {
          await userModelGroupMappingService.updateUserModelGroupMapping({
            ...base, userIds: toDeactivate.map(Number), isActive: false, updatedBy: currentUserId,
          });
        }
      } else {
        const existingUserIds = existingMappings.map(m => String(m.userId));
        const toAdd           = checkedUserIds.filter(uid => !existingUserIds.includes(uid));
        const toActivate      = existingMappings.filter(m => checkedUserIds.includes(String(m.userId)) && !m.isActive).map(m => String(m.userId));
        const toDeactivate    = existingMappings.filter(m => !checkedUserIds.includes(String(m.userId)) && m.isActive).map(m => String(m.userId));

        if (toAdd.length > 0) {
          await userModelGroupMappingService.createUserModelGroupMapping({
            ...base,
            userIds:   toAdd.map(Number),
            createdBy: currentUserId,
          });
        }
        if (toActivate.length > 0) {
          await userModelGroupMappingService.updateUserModelGroupMapping({
            ...base,
            userIds:   toActivate.map(Number),
            isActive:  true,
            updatedBy: currentUserId,
          });
        }
        if (toDeactivate.length > 0) {
          await userModelGroupMappingService.updateUserModelGroupMapping({
            ...base,
            userIds:   toDeactivate.map(Number),
            isActive:  false,
            updatedBy: currentUserId,
          });
        }
      }

      showToast(isEdit ? "Assignment updated!" : "Assignment created!", "success");
      setTimeout(() => onSuccess(), 1200);
    } catch (e) {
      console.error(e);
      showToast("Failed to save. Please try again.", "error");
    } finally { setIsSubmitting(false); }
  };

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email.toLowerCase().includes(userSearch.toLowerCase())
  );
  const allFilteredChecked = filteredUsers.length > 0 && filteredUsers.every(u => checkedUserIds.includes(u.id));
  const selectedCompany = companies.find(c => c.id === companyId);
  const selectedGroup   = modelGroups.find(g => String(g.id) === String(modelGroupId));

  return (
    <>
      <div className="bg-white dark:bg-slate-800 rounded-[2rem] shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden animate-in fade-in duration-300 flex flex-col max-h-[calc(100vh-140px)]">

        {/* ── Header ── */}
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 bg-gradient-to-r from-[#1d57a5]/5 to-transparent dark:from-[#1d57a5]/10 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#1d57a5]/10 flex items-center justify-center">
                <UserCheck size={20} className="text-[#1d57a5]" />
              </div>
              <div>
                <h2 className="text-base font-bold text-slate-800 dark:text-white">
                  {isEdit ? "Edit Assignment" : "New Assignment"}
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Assign model groups to users by company
                </p>
              </div>
            </div>
            <button onClick={onCancel} className="p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
              <X size={18} />
            </button>
          </div>

          {/* Step indicators */}
          <div className="flex items-center gap-4 mt-4">
            <StepBadge number={1} label="Company"     active={activeStep === 1} done={step1Done} />
            <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
            <StepBadge number={2} label="Model Group" active={activeStep === 2} done={step2Done} />
            <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
            <StepBadge number={3} label="Users"       active={activeStep === 3} done={false} />
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* Company + Model Group row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <StyledSelect
              label="Company"
              required
              icon={Building2}
              value={companyId}
              onChange={setCompanyId}
              loading={loadingCompanies}
              disabled={isEdit}
              hint={isEdit ? "Cannot change company when editing" : ""}
            >
              <option value="">— Select Company —</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </StyledSelect>

            <StyledSelect
              label="Model Group"
              required
              icon={Cpu}
              value={modelGroupId}
              onChange={setModelGroupId}
              disabled={!companyId}
              loading={loadingGroups}
              hint={!companyId ? "Select a company first" : ""}
            >
              <option value="">— Select Model Group —</option>
              {modelGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </StyledSelect>
          </div>

          {/* Selection summary pills */}
          {(selectedCompany || selectedGroup) && (
            <div className="flex flex-wrap gap-2">
              {selectedCompany && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#1d57a5]/10 text-[#1d57a5] dark:text-[#4a8ce3] rounded-full text-xs font-semibold">
                  <Building2 size={11} /> {selectedCompany.name}
                </span>
              )}
              {selectedGroup && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full text-xs font-semibold">
                  <Cpu size={11} /> {selectedGroup.name}
                </span>
              )}
            </div>
          )}

          {/* Users section */}
          <div className="bg-slate-50 dark:bg-slate-900/30 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            {/* Users header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
              <div className="flex items-center gap-2">
                <Users size={15} className="text-slate-500" />
                <span className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Users
                </span>
                {checkedUserIds.length > 0 && (
                  <span className="px-2 py-0.5 bg-[#1d57a5] text-white text-[10px] font-bold rounded-full">
                    {checkedUserIds.length} selected
                  </span>
                )}
              </div>
              {filteredUsers.length > 0 && (
                <button type="button" onClick={toggleAll} className="text-xs font-semibold text-[#1d57a5] hover:underline">
                  {allFilteredChecked ? "Deselect All" : "Select All"}
                </button>
              )}
            </div>

            <div className="p-4">
              {/* Empty / loading states */}
              {!companyId || !modelGroupId ? (
                <div className="text-center py-8">
                  <Users size={32} className="mx-auto text-slate-300 dark:text-slate-600 mb-2" />
                  <p className="text-xs text-slate-400">{!companyId ? "Select a company first" : "Select a model group to see users"}</p>
                </div>
              ) : loadingUsers || loadingMappings ? (
                <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-500">
                  <Loader2 size={18} className="animate-spin text-[#1d57a5]" /> Loading users…
                </div>
              ) : (
                <>
                  {/* Search */}
                  {users.length > 0 && (
                    <div className="relative mb-3">
                      <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                      <input
                        type="text"
                        placeholder="Search users…"
                        value={userSearch}
                        onChange={e => setUserSearch(e.target.value)}
                        className="w-full pl-8 pr-4 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-[#1d57a5] outline-none transition-all"
                      />
                    </div>
                  )}

                  {filteredUsers.length === 0 ? (
                    <div className="text-center py-6">
                      <p className="text-xs text-slate-400">{userSearch ? "No users match your search" : "No users found for this company"}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-1">
                      {filteredUsers.map(user => {
                        const checked = checkedUserIds.includes(user.id);
                        return (
                          <div
                            key={user.id}
                            onClick={() => toggleUser(user.id)}
                            className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all select-none ${
                              checked
                                ? "border-[#1d57a5] bg-[#1d57a5]/5 dark:bg-[#1d57a5]/20"
                                : "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 hover:border-[#1d57a5]/40 hover:bg-slate-100 dark:hover:bg-slate-700/50"
                            }`}
                          >
                            {/* Avatar */}
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                              checked ? "bg-[#1d57a5] text-white" : "bg-slate-100 dark:bg-slate-700 text-slate-500"
                            }`}>
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-semibold truncate ${checked ? "text-[#1d57a5] dark:text-[#4a8ce3]" : "text-slate-700 dark:text-slate-300"}`}>
                                {user.name}
                              </p>
                              {user.email && <p className="text-[10px] text-slate-400 truncate">{user.email}</p>}
                            </div>
                            {/* Checkbox */}
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                              checked ? "bg-[#1d57a5] border-[#1d57a5]" : "border-slate-300 dark:border-slate-500 bg-transparent"
                            }`}>
                              {checked && <Check size={10} strokeWidth={3} className="text-white" />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/20 flex-shrink-0">
          <button onClick={onCancel} className="px-5 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors rounded-full">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !companyId || !modelGroupId}
            className="flex items-center gap-2 px-6 py-2.5 text-sm font-semibold bg-[#36a852] hover:bg-[#2e9046] disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-full transition-all shadow-sm"
          >
            {isSubmitting
              ? <><Loader2 size={14} className="animate-spin" /> Saving…</>
              : <><CheckSquare size={14} /> {isEdit ? "Update Assignment" : "Save Assignment"}</>
            }
          </button>
        </div>
      </div>

      {toast.visible && (
        <div className="fixed top-6 right-6 z-[9999] flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-xl text-sm font-semibold animate-in slide-in-from-top-4 duration-300 bg-slate-900 dark:bg-white text-white dark:text-slate-900">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${toast.type === "success" ? "bg-[#36a852]" : "bg-red-500"}`}>
            {toast.type === "success" ? <Check size={12} strokeWidth={3} /> : <XCircle size={12} />}
          </div>
          {toast.message}
        </div>
      )}
    </>
  );
}
